import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Box,
  Alert,
  Typography,
} from "@mui/material";
import AcpeValidationGrid from "./AcpeValidationGrid";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useState } from "react";
import { useQuery } from "react-query";
import { ISkuQualificationRequest } from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import { FORM_ERROR } from "final-form";

const getStyles = () => ({
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: 1,
  },
});

export type AcpeValidationDialogProps = {
  open: boolean;
  skuQualificationRequest: ISkuQualificationRequest | null;
  onClose: () => void;
  onSave: (
    releaseDate?: Date,
    expirationDate?: Date,
    contactHours?: number,
  ) => void | Promise<any>;
};

const AcpeValidationDialog = ({
  open,
  skuQualificationRequest,
  onClose,
  onSave,
}: AcpeValidationDialogProps) => {
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const { qualificationService } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const styles = getStyles();

  const { data: response, isLoading } = useQuery(
    [
      "qualificationService.validateNabpUan",
      skuQualificationRequest?.courseNumber,
    ],
    () =>
      qualificationService.validateNabpUan(
        skuQualificationRequest?.courseNumber,
      ),
    { enabled: !!skuQualificationRequest?.courseNumber },
  );

  const handleSave = async () => {
    setSaving(true);
    setSubmitError(undefined);
    try {
      const error = await onSave(
        response?.releaseDate,
        response?.expirationDate,
        response?.contactHours,
      );

      if (error && error[FORM_ERROR]) {
        setSubmitError(error[FORM_ERROR]);
      } else {
        handleClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSubmitError(undefined);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <T id="validatingCourseInformationWithNabp" />
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={styles.loadingBox}>
            <T
              id="courseNumberBeingValidated"
              values={{ courseNumber: skuQualificationRequest?.courseNumber }}
            />
            <CircularProgress />
          </Box>
        ) : response ? (
          <>
            <Typography mb={1}>
              <T id="courseNumber" />: {skuQualificationRequest?.courseNumber}
            </Typography>
            {response.isValid === false ? (
              <Alert severity="error">
                {t({ id: "nabpValidationError" })}: {response.errorMessage}
              </Alert>
            ) : (
              <AcpeValidationGrid
                response={response}
                skuQualificationRequest={skuQualificationRequest}
              />
            )}

            {submitError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {submitError}
              </Alert>
            )}
          </>
        ) : (
          <T id="fetchError" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={saving}>
          <T id="cancelButton" />
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={
            saving || isLoading || (response && response.isValid === false)
          }
          startIcon={saving && <CircularProgress size={16} />}
        >
          <T id="skuAccreditationBodySaveButtonLabel" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AcpeValidationDialog;
